import ImageKit from "imagekit";

export async function uploadImage(base64Image, builderName) {
  const now = new Date();
  const yearMonth = `${now.getFullYear()}-${String(
    now.getMonth() + 1,
  ).padStart(2, "0")}`;

  return uploadBase64File(base64Image, {
    folder: `builder/uploads/${yearMonth}/${builderName}/`,
    prefix: `${builderName}-`,
    tags: [builderName],
  });
}

export async function uploadProductScreenshot(base64Image, builderName) {
  const now = new Date();
  const yearMonth = `${now.getFullYear()}-${String(
    now.getMonth() + 1,
  ).padStart(2, "0")}`;
  const resizedImage = await resizeBase64Image(base64Image, 600, "image/webp");

  return uploadBase64File(resizedImage, {
    folder: `/builder/uploads/${yearMonth}/product-screenshots/`,
    prefix: `${builderName}-preview-`,
    tags: [builderName, "product-screenshot"],
    extension: "webp",
    transformation: { pre: "w-600,f-webp,q-80" },
  });
}

function resizeBase64Image(base64Image, maxWidth, outputMimeType) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const targetWidth = Math.min(image.naturalWidth, maxWidth);
      const scale = targetWidth / image.naturalWidth;
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = Math.round(image.naturalHeight * scale);

      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Could not create a canvas context."));
        return;
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL(outputMimeType, 0.8));
    };

    image.onerror = () => reject(new Error("Could not resize the screenshot."));
    image.src = base64Image;
  });
}

async function uploadBase64File(
  base64Image,
  { folder, prefix, tags, extension: forcedExtension, transformation },
) {
  const imagekit = new ImageKit({
    publicKey: process.env.IMAGEKIT_PUBLIC_KEY,
    privateKey: process.env.IMAGEKIT_PRIVATE_KEY,
    urlEndpoint: process.env.IMAGEKIT_URL,
  });

  const mimeMatch = base64Image.match(/^data:(image\/[a-zA-Z]+);base64,/);
  const mimeType = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const extension = forcedExtension || mimeType.split("/")[1];
  const imageData = base64Image.split(",")[1];

  const fileName = generateRandomFileName(extension, prefix);

  const uploadOptions = {
    file: imageData, // base64 data only
    fileName,
    folder,
    useUniqueFileName: true,
    tags,
    ...(transformation && { transformation }),
  };

  try {
    const result = await imagekit.upload(uploadOptions);
    return result.url;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw new Error("Failed to upload the file.");
  }
}

export async function uploadJson(jsonObject, builderName) {
  return uploadJsonFile(jsonObject, {
    builderName,
    category: "poster-config",
    prefix: `${builderName}-`,
    tags: [builderName, "songtext-pdf-data"],
  });
}

export async function uploadGeoJson(geojsonObject, builderName = "sportsmap") {
  return uploadJsonFile(geojsonObject, {
    builderName,
    category: "routes",
    getFolder: (yearMonth) => `builder/uploads/${yearMonth}/routes/`,
    prefix: "route-",
    extension: "geojson",
    tags: [builderName, "sportsmap-route"],
  });
}

async function uploadJsonFile(
  jsonObject,
  { builderName, category, getFolder, prefix, extension = "json", tags },
) {
  const imagekit = new ImageKit({
    publicKey: process.env.IMAGEKIT_PUBLIC_KEY,
    privateKey: process.env.IMAGEKIT_PRIVATE_KEY,
    urlEndpoint: process.env.IMAGEKIT_URL,
  });

  // Turn object -> string -> base64
  const jsonString = JSON.stringify(jsonObject, null, 2);
  const base64Data = Buffer.from(jsonString).toString("base64");

  const now = new Date();
  const yearMonth = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}`;
  const folder = getFolder
    ? getFolder(yearMonth)
    : `builder/${category}/${yearMonth}/${builderName}/`;
  const fileName = generateRandomFileName(extension, prefix);

  const uploadOptions = {
    file: base64Data, // base64-encoded string (WITHOUT data: prefix)
    fileName, // ends with .json
    folder,
    useUniqueFileName: true,
    tags,
  };

  try {
    const result = await imagekit.upload(uploadOptions);
    return result.url; // <-- This is what we'll send in payload.pdf.url
  } catch (error) {
    console.error("Error uploading JSON file:", error);
    throw new Error("Failed to upload the JSON file.");
  }
}

function generateRandomFileName(extension, prefix = "") {
  const random = Math.random().toString(36).substring(2, 10);
  return `${prefix}${random}.${extension}`;
}
